import { component$, Resource, useResource$ } from "@builder.io/qwik";
import { fetchEntries, Image } from "@builder.io/sdk-qwik";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardTitle,
} from "~/components/ui/card";
import { cn } from "~/lib/cn";

interface Article {
  title: string;
  blurb: string;
  image: string;
  slug: string;
}

export const BUILDER_PUBLIC_API_KEY = import.meta.env.VITE_BUILDER_API_KEY!;

const articlesPerPage = 30;

export const BlogGrid = component$(
  (props: {
    query?: Record<string, any>;
    class?: string;
    limit?: number;
    heading?: string;
  }) => {
    const articlesRsrc = useResource$(() =>
      fetchEntries({
        model: "blog-article",
        apiKey: BUILDER_PUBLIC_API_KEY,
        options: {
          limit: props.limit ?? articlesPerPage,
          includeRefs: true,
          enrich: true,
          omit: "data.blocks",
        },
        query: props.query,
      }),
    );

    return (
      <Resource
        value={articlesRsrc}
        onPending={() => <div>Loading articles...</div>}
        onResolved={(articles) => {
          if (articles.length === 0) {
            return null;
          }

          return (
            <div class={cn("flex w-full flex-col gap-4", props.class)}>
              {props.heading && (
                <h2 class="font-heading text-primary text-2xl font-semibold tracking-tight">
                  {props.heading}
                </h2>
              )}
              <div class={cn("grid grid-cols-1 gap-4 lg:grid-cols-2")}>
                {articles
                  ?.filter((a) => !!a.data)
                  .map((a) => a.data as Article)
                  .map((article) => (
                    <a
                      href={`/blog/${article.slug}`}
                      key={article.slug}
                      class="group"
                    >
                      <Card>
                        {article.image && (
                          <div class="mx-4">
                            <Image
                              // eslint-disable-next-line qwik/no-react-props
                              className="mx-auto mt-4 h-32 w-full rounded object-cover"
                              height={128}
                              image={article.image}
                            />
                          </div>
                        )}
                        <CardContent>
                          <CardTitle>{article.title}</CardTitle>
                          <CardDescription>{article.blurb}</CardDescription>
                          <CardFooter>
                            <span class="text-sm text-gray-600 underline group-hover:no-underline">
                              Read more&hellip;
                            </span>
                          </CardFooter>
                        </CardContent>
                      </Card>
                    </a>
                  ))}
              </div>
            </div>
          );
        }}
      />
    );
  },
);
